import { datadogLogs } from '@datadog/browser-logs';
import {
  type ApplyBlockReason,
  decideAccountAllocationApply,
} from '@ovComponents/4-module/workflowCompletion/subSteps/editAccountAllocations/applyAccountAllocationsChecks';
import { useCommitAccountAllocations } from '@ovComponents/4-module/workflowCompletion/subSteps/editAccountAllocations/commitAccountAllocations';
import { useEditAccountAllocations } from '@ovComponents/4-module/workflowCompletion/subSteps/editAccountAllocations/hooks/useEditAccountAllocations';
import { useStagedAccountAllocations } from '@ovComponents/4-module/workflowCompletion/subSteps/editAccountAllocations/hooks/useStagedAccountAllocations';
import { useEffect, useRef, useState } from 'react';

export type ApplyAccountAllocationsState =
  | { status: 'APPLYING' }
  | { status: 'BLOCKED'; reason: ApplyBlockReason }
  | { status: 'FAILED'; code: string };

type UseApplyAccountAllocationsInput = {
  householdClientGroupId: string | undefined;
  onNext: () => Promise<unknown> | void;
};

const APPLYING = { status: 'APPLYING' } satisfies ApplyAccountAllocationsState;

export const useApplyAccountAllocations = ({
  householdClientGroupId,
  onNext,
}: UseApplyAccountAllocationsInput) => {
  const { report, loading, error, refetch } = useEditAccountAllocations(
    householdClientGroupId,
  );
  const { state: stagedState } = useStagedAccountAllocations({
    householdClientGroupId,
    report,
  });
  const commit = useCommitAccountAllocations();

  const [state, setState] = useState<ApplyAccountAllocationsState>(APPLYING);
  const [attempt, setAttempt] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  // Guards one run per attempt across re-renders. It does not survive a
  // remount; the already-applied check in the decision covers that.
  const ranAttempt = useRef<number | null>(null);
  // Closing the modal mid-apply and reopening it mounts a second step that
  // advances on its own, so an unmounted one must not advance too. This is
  // its own effect because the apply effect re-runs on most renders.
  const mounted = useRef(false);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (loading || refreshing || ranAttempt.current === attempt) return;
    ranAttempt.current = attempt;

    const block = (reason: ApplyBlockReason) => {
      datadogLogs.logger.warn('Apply account allocations blocked', {
        clientGroupId: householdClientGroupId,
        reason,
      });
      setState({ status: 'BLOCKED', reason });
    };

    const fail = (code: string, cause?: Error) => {
      datadogLogs.logger.error(
        'Apply account allocations failed',
        { clientGroupId: householdClientGroupId, code },
        cause,
      );
      setState({ status: 'FAILED', code });
    };

    const apply = async () => {
      if (error) return fail('generic', error);
      if (stagedState.status === 'ABSENT') {
        return block({ code: 'MISSING_SNAPSHOT' });
      }
      // No report, or one with no accounts left, means the report the
      // allocation was staged against is gone.
      if (!report || stagedState.status === 'LOADING') {
        return block({ code: 'STALE_REPORT' });
      }

      const decision = decideAccountAllocationApply(stagedState, report);
      if (decision.status === 'BLOCKED') return block(decision.reason);
      if (decision.status === 'COMMIT') {
        const result = await commit(decision.staged, report);
        if (result.status === 'FAILED') return fail(result.code, result.cause);
      }
      if (!mounted.current) return;
      datadogLogs.logger.info('Apply account allocations applied', {
        clientGroupId: householdClientGroupId,
        decision: decision.status,
      });
      // NEXT_STEP rethrows on failure. Retry then reads the write as already
      // applied and only advances.
      try {
        await onNext();
      } catch (nextStepError) {
        fail(
          'generic',
          nextStepError instanceof Error ? nextStepError : undefined,
        );
      }
    };

    void apply();
  }, [
    attempt,
    loading,
    refreshing,
    error,
    report,
    stagedState,
    commit,
    onNext,
    householdClientGroupId,
  ]);

  // Refetched first so a commit that landed before the failure was reported
  // reads as already applied instead of being written again.
  const retry = () => {
    setState(APPLYING);
    setRefreshing(true);
    refetch()
      .catch(() => undefined)
      .finally(() => {
        setRefreshing(false);
        setAttempt((previous) => previous + 1);
      });
  };

  return { state, report, retry };
};
